// The ground on screen: nested rings of the server's height windows (groundClip.ts) drawn as a geometry clipmap, and "how high is the ground here" for the walking camera.
//
// Each ring is one mesh on its window's own lattice: a grid of RING_N x RING_N vertices whose positions are the integer lattice indices, laid at the window's corner, and whose heights, normals and
// classes the vertex shader reads from the ring's layer of the height array (groundClip.ts GROUND_GLSL has the same layers for everything else that needs the ground). Nothing is re-sampled as the eye moves:
// a window stays put until the eye is out of its middle half and the server sends the next. Which quads of a ring are drawn (its outer edge to the hole the finer ring fills), and how its heights blend into
// the coarser ring's near the outer edge so the rings meet without a crack, is worked out in groundClip.ts (RingLayout); this file turns it into index buffers and draws it.
//
// Surface: the land is the server's (Big Island of Hawaii, from the land file's terrain artist), and the colour here is a stand-in until the geology exists. It is a smooth function of the ground
// itself, never of a cell: each vertex carries the ground's slope measured over about 150 m (groundClip.ts groundSlopeRel), the fragment shader turns it and the fine height into weights of a small
// palette (coastal flat, gentle, moderate, steep: groundWeights, the same weights the grass uses), takes the sea floor where the fine height is under the sea's level (the waterline is the true contour, not
// a cell edge), exposes rock on slopes and bare lava high up, and adds world-space noise for patches and a fine bump that fades out with distance. (The artist's class byte still comes with the windows and
// still sets the artist's detail; the colour no longer reads it: a class is one value per 148 m cell, and as a colour it drew the cells.) A wet band follows the waterline: ground at and below the tide's
// recent high mark is darker and glossier, and dries slowly as the tide falls (setWetLevel).
//
// The sea is drawn by waterView.ts over the sea floor. Sim (x, y, z) maps to three (x, z, -y).
import * as THREE from 'three';
import { CanopyShade, CANOPY_SHADE_DECL, canopyShadeApply } from './canopyShade';
import { CanopyClip } from './canopyClip';
import { CANOPY_LOOK_GLSL } from './canopyLook';
import { GROUND_GLSL, GroundClip, MORPH_CELLS, type IRect, type RingLayout } from './groundClip';
import { RINGS, RING_N } from './groundWire';
import { origin } from './origin';
import { dropAt, planet } from './planet';

/** What the server says about the sea: its level (m above the datum, 0 for the real mean sea level), the tide's peak, and the size of the patch the physics and the plants live on. */
export interface GroundInfo {
  type: 'ground_info';
  sea_level: number;
  tide_amplitude: number;
  width: number; height: number;
  /** From the land file's artist block: the slope (rise over run) between gentle and moderate ground and between moderate and steep, and the height of the coastal flats (m). Present whenever the server sends ground windows. */
  slope_gentle?: number; slope_moderate?: number; beach_h?: number;
}

// Ground colours (hex sRGB): the class palette, then the bare lava and the summit cinder.
const PALETTE = [0x3c3b38, 0xd2c6a6, 0x978c5a, 0x7a6c4e, 0x5c534a, 0x3d3a38, 0x70503f];
const BARE_LAVA_FROM_M = 1500, BARE_LAVA_FULL_M = 2500;
const CINDER_FROM_M = 3300, CINDER_FULL_M = 3900;
const ROCK_FROM = 0.12, ROCK_FULL = 0.35;      // 1 - normal.y: 30 degrees is 0.134, 45 degrees 0.29
const GROUND_ALBEDO = 0.9;
const GROUND_ROUGH = 0.92;
const GROUND_SPECULAR = 0.3;
const WET_DARKEN = 0.58;
const WET_ROUGH = 0.55;
const WET_SPEC = 3.2;
const WET_SOAK_BELOW = 0.02;     // metres: ground this far under the wet level is fully soaked
const WET_FADE_ABOVE = 0.10;     // metres: the soaked look fades out over this height above it
const BUMP_AMPLITUDE_M = 0.02;   // height of the fine bump
const BUMP_FADE: [number, number] = [6, 40];   // metres from the eye over which the bump fades out
const PARAMS_WAIT_MS = 5000;     // how long the first window may come before the ground_info does
const SHADOW_RINGS_DEFAULT = 7;  // rings 0 to this cast shadows (the cascades reach a few hundred metres)

// ---------------------------------------------------------------------------------------
// Vertex stage: heights, normals and class weights straight from the ring's layer.
// ---------------------------------------------------------------------------------------
const RING_VERT_DECL = /* glsl */`
${GROUND_GLSL}
uniform int   uRingK;
uniform float uDx;
uniform vec4  uBand;      // the drawn rectangle's i0, j0, i1, j1
uniform vec2  uUp;        // this ring's vertex (0, 0) in the next coarser ring's cell coordinates
uniform float uHasUp;
varying vec3  vGPos;
varying float vGMacro;    // the ground's slope over about 150 m (rise over run)
varying float vGSlope;    // 1 - normal.y
float gcH;
vec3  gcN;
float gcBandWeight(ivec2 ij) {
  float d = min(min(float(ij.x) - uBand.x, uBand.z - float(ij.x)), min(float(ij.y) - uBand.y, uBand.w - float(ij.y)));
  return 1.0 - smoothstep(0.0, ${MORPH_CELLS.toFixed(1)}, max(d, 0.0));
}
float gcHeight(ivec2 ij) {
  ij = clamp(ij, ivec2(0), ivec2(${RING_N - 1}));
  float h = texelFetch(uGH, ivec3(ij, uRingK), 0).r;
  if (uHasUp > 0.5) {
    float w = gcBandWeight(ij);
    if (w > 0.0) h = mix(h, groundBilinear(uRingK + 1, uUp + 0.5 * vec2(ij)), w);
  }
  return h;
}
void gcCompute(bool full) {
  ivec2 ij = ivec2(position.xz + 0.5);
  gcH = gcHeight(ij);
  if (full) {
    float hE = gcHeight(ij + ivec2(1, 0)), hW = gcHeight(ij - ivec2(1, 0));
    float hN = gcHeight(ij + ivec2(0, 1)), hS = gcHeight(ij - ivec2(0, 1));
    float sx = float(min(ij.x + 1, ${RING_N - 1}) - max(ij.x - 1, 0)), sy = float(min(ij.y + 1, ${RING_N - 1}) - max(ij.y - 1, 0));
    gcN = normalize(vec3(-(hE - hW) / (sx * uDx), 1.0, (hN - hS) / (sy * uDx)));      // x east; j is tile y, which is minus three z
  }
}
`;
const RING_VERT_NORMAL = /* glsl */`
gcCompute(true);
vec3 objectNormal = gcN;
`;
const RING_VERT_BEGIN = /* glsl */`
vec3 transformed = vec3(position.x * uDx, gcH, -position.z * uDx);
vGPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
vGMacro = groundSlope(vGPos.xz);
vGSlope = 1.0 - gcN.y;
`;
const DEPTH_VERT_BEGIN = /* glsl */`
gcCompute(false);
vec3 transformed = vec3(position.x * uDx, gcH, -position.z * uDx);
`;

// ---------------------------------------------------------------------------------------
// Fragment stage: palette, patches, wet band and the fine bump.
// ---------------------------------------------------------------------------------------
const GROUND_FRAG_COMMON = /* glsl */`
#include <common>
${CANOPY_SHADE_DECL}
${GROUND_GLSL}
${CANOPY_LOOK_GLSL}
varying vec3  vGPos;
varying float vGMacro;
varying float vGSlope;
uniform vec3  uGPal[${PALETTE.length}];
uniform float uGWetY;        // three-y of the wet level: the sea's recent high mark, easing down as the tide falls
uniform float uGAlbedo;
uniform float uGRough;
uniform float uGSpec;
uniform float uGBump;        // 0 to 1: how far out the fine bump reaches (setDetail)
uniform vec2  uGBumpFade;

float gRoughF;
float gWet;
float gCan = 0.0;      // how much of the ground the far canopy hides (canopyLook.ts)
vec3  gNormTS;

float gHash21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float gVnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(gHash21(i), gHash21(i + vec2(1.0, 0.0)), f.x),
             mix(gHash21(i + vec2(0.0, 1.0)), gHash21(i + vec2(1.0, 1.0)), f.x), f.y);
}
float gBump(vec2 p) { return 0.6 * gVnoise(p * 7.0) + 0.3 * gVnoise(p * 19.0 + 3.7) + 0.1 * gVnoise(p * 53.0 + 9.1); }
`;

const GROUND_FRAG_COLOR = /* glsl */`
#include <color_fragment>
{
  float dist = length(vViewPosition);
  // Broad, world-space variation: drier and wetter patches a few metres across.
  float mN = 0.55 * gVnoise(vGPos.xz / 9.0) + 0.30 * gVnoise(vGPos.xz / 3.3 + 11.7) + 0.15 * gVnoise(vGPos.xz / 1.25 + 5.1);
  float macroLum = 0.86 + 0.28 * mN;
  float h = vGPos.y;
  // land or sea floor: the contour of the fine height at the sea's level, blended over the width of a pixel
  float hs = h - uGPar.w;
  float fw = max(fwidth(hs), 1.0e-4);
  float land = smoothstep(-fw, fw, hs);
  vec4 gk = groundWeights(vGMacro, h);
  vec3 base = mix(uGPal[0], uGPal[1] * gk.x + uGPal[2] * gk.y + uGPal[3] * gk.z + uGPal[4] * gk.w, land);
  base = mix(base, uGPal[4], smoothstep(${ROCK_FROM.toFixed(2)}, ${ROCK_FULL.toFixed(2)}, vGSlope) * land);
  base = mix(base, uGPal[5], smoothstep(${BARE_LAVA_FROM_M.toFixed(1)}, ${BARE_LAVA_FULL_M.toFixed(1)}, h) * land);
  base = mix(base, uGPal[6], smoothstep(${CINDER_FROM_M.toFixed(1)}, ${CINDER_FULL_M.toFixed(1)}, h) * land);
  gWet = 1.0 - smoothstep(uGWetY - ${WET_SOAK_BELOW.toFixed(3)}, uGWetY + ${WET_FADE_ABOVE.toFixed(3)}, h);
  gRoughF = uGRough * (1.0 - 0.05 * (1.0 - mN));
  gNormTS = vec3(0.0, 0.0, 1.0);
  // the fine bump: a procedural height of a few centimetres, seen as a tilt of the lit normal, faded out with distance (and, being much finer than the rings farther out, never there)
  float bf = (1.0 - smoothstep(uGBumpFade.x, uGBumpFade.y, dist)) * uGBump;
  float grain = 1.0;
  if (bf > 0.002) {
    vec2 p = vGPos.xz;
    float e = 0.012;
    vec2 g = vec2(gBump(p + vec2(e, 0.0)) - gBump(p - vec2(e, 0.0)), gBump(p + vec2(0.0, e)) - gBump(p - vec2(0.0, e))) / (2.0 * e);
    gNormTS = normalize(vec3(-g * ${BUMP_AMPLITUDE_M.toFixed(3)} * bf, 1.0));
    grain = 1.0 + (gBump(p * 1.7) - 0.5) * 0.35 * bf;
  }
  // far ground is calm: the roughness of the relief that is too small to see
  gRoughF = mix(1.0, gRoughF, bf);
  diffuseColor.rgb = base * macroLum * grain * uGAlbedo;
  diffuseColor.rgb *= mix(1.0, ${WET_DARKEN.toFixed(3)}, gWet);
  gRoughF *= mix(1.0, ${WET_ROUGH.toFixed(3)}, gWet);
  // the far canopy over the ground: crowns while a pixel is smaller than one, their mean tint beyond (canopyLook.ts)
  {
    float fpx = length(fwidth(vGPos.xz));
    float cov;
    vec3 nCan;
    vec3 sunW = vec3(0.4, 0.8, 0.3);
#if NUM_DIR_LIGHTS > 0
    sunW = normalize(inverseTransformDirection(directionalLights[0].direction, viewMatrix));
#endif
    diffuseColor.rgb = canopyPaint(diffuseColor.rgb, vGPos.xz, vGPos.xz - uGOrg, dist, fpx, land, sunW, cov, nCan);
    gNormTS = normalize(gNormTS + (nCan - vec3(0.0, 0.0, 1.0)));
    gCan = cov;
    gRoughF = mix(gRoughF, 1.0, gCan);
  }
}
`;

const GROUND_FRAG_ROUGH = /* glsl */`
float roughnessFactor = clamp(gRoughF, 0.0, 1.0);
`;

const GROUND_FRAG_SPEC = /* glsl */`
#include <lights_fragment_end>
reflectedLight.directSpecular   *= uGSpec * mix(1.0, ${WET_SPEC.toFixed(2)}, gWet) * (1.0 - 0.85 * gCan);
reflectedLight.indirectSpecular *= uGSpec * mix(1.0, ${WET_SPEC.toFixed(2)}, gWet) * (1.0 - 0.85 * gCan);
${canopyShadeApply('vGPos')}
`;

// The bump tilts the lit normal in a frame built from the world axes (u = +x, v = -z, so the map's +Y faces sim +y).
const GROUND_FRAG_NORMAL = /* glsl */`
#include <normal_fragment_maps>
{
  vec3 nW = inverseTransformDirection(normal, viewMatrix);
  vec3 T = normalize(vec3(1.0, 0.0, 0.0) - nW * nW.x);
  vec3 B = cross(nW, T);
  vec3 pW = normalize(T * gNormTS.x + B * gNormTS.y + nW * gNormTS.z);
  normal = normalize((viewMatrix * vec4(pW, 0.0)).xyz);
}
`;

/** The index buffer of the quads a ring draws: the rectangle `o` of vertex indices, less the quads inside `hole`. */
function ringIndex(o: IRect, hole: IRect | null): Uint16Array {
  const cols = o[2] - o[0], rows = o[3] - o[1];
  let quads = cols * rows;
  if (hole) quads -= (hole[2] - hole[0]) * (hole[3] - hole[1]);
  const idx = new Uint16Array(Math.max(0, quads) * 6);
  let t = 0;
  for (let j = o[1]; j < o[3]; j++) {
    const inHoleRow = hole !== null && j >= hole[1] && j < hole[3];
    for (let i = o[0]; i < o[2]; i++) {
      if (inHoleRow && i >= hole![0] && i < hole![2]) { i = hole![2] - 1; continue; }
      const a = j * RING_N + i, b = a + 1, d = a + RING_N, e = d + 1;
      // counter-clockwise seen from above (three +y): i is east, j is tile north (three -z)
      idx[t++] = a; idx[t++] = b; idx[t++] = d;
      idx[t++] = b; idx[t++] = e; idx[t++] = d;
    }
  }
  return idx;
}

interface RingMesh {
  mesh: THREE.Mesh;
  geo: THREE.BufferGeometry;
  mat: THREE.MeshStandardMaterial;
  depth: THREE.MeshDepthMaterial;
  u: {
    uRingK: { value: number }; uDx: { value: number }; uBand: { value: THREE.Vector4 };
    uUp: { value: THREE.Vector2 }; uHasUp: { value: number };
  };
  box: THREE.Box3;
}

export class TerrainView {
  private readonly scene: THREE.Scene;
  private readonly plane: THREE.Mesh;          // the flat fallback, drawn only until the first window comes
  private readonly grid: THREE.Object3D | null;
  private readonly clip: GroundClip;
  private readonly canopy: CanopyClip;
  private readonly ownsCanopy: boolean;
  private readonly rings: RingMesh[] = [];
  private readonly positions: THREE.BufferAttribute;
  private readonly shade: CanopyShade;
  private readonly ownsShade: boolean;
  private readonly wetY = { value: -1e3 };      // three-y of the wet level
  private readonly bump = { value: 1 };
  private readonly palette: THREE.Color[] = PALETTE.map(h => new THREE.Color(h));
  private groundOrder = 1;
  private shadowRings = SHADOW_RINGS_DEFAULT;
  private builtVersion = -1;
  private paramsSince = -1;          // performance.now() when the ground first had to wait for its parameters, -1 when it is not waiting
  private info: GroundInfo | null = null;
  private readonly frustum = new THREE.Frustum();
  private readonly pv = new THREE.Matrix4();
  private readonly box = new THREE.Box3();
  private drawn = 0;

  /** `shade` (optional) scales the lit ground by the plant canopy's shading map. Without one the shader still compiles the same, with a neutral map that leaves the ground unshaded. */
  constructor(scene: THREE.Scene, plane: THREE.Mesh, grid: THREE.Object3D | null, clip: GroundClip, shade: CanopyShade | null = null, canopy: CanopyClip | null = null) {
    this.scene = scene; this.plane = plane; this.grid = grid; this.clip = clip;
    this.ownsCanopy = canopy === null;
    this.canopy = canopy ?? new CanopyClip();
    this.ownsShade = shade === null;
    this.shade = shade ?? new CanopyShade(1, 1);
    const pos = new Float32Array(RING_N * RING_N * 3);
    for (let j = 0; j < RING_N; j++) for (let i = 0; i < RING_N; i++) { const k = (j * RING_N + i) * 3; pos[k] = i; pos[k + 2] = j; }
    this.positions = new THREE.BufferAttribute(pos, 3);
    for (let k = 0; k < RINGS; k++) this.rings.push(this.makeRing(k));
  }

  /** True once there is ground to stand on (the first window has come). */
  get loaded(): boolean { return this.clip.ready; }
  /** Sea level (three y) before tides, from the server's ground_info (0 until it comes). */
  get seaLevel(): number { return this.info?.sea_level ?? 0; }
  /** Peak tide excursion in metres. */
  get tideAmplitude(): number { return this.info?.tide_amplitude ?? 0; }
  /** True when the ground dips below sea level somewhere in what has been sent, i.e. there is an ocean to draw. */
  get hasSea(): boolean { return this.clip.ready && this.clip.minHeight < this.seaLevel - 0.05; }
  /** The lowest ground in the windows received. */
  get minHeight(): number { return this.clip.ready ? this.clip.minHeight : 0; }
  /** Rings drawn in the last frame (for the overlay). */
  get ringsDrawn(): number { return this.drawn; }

  setInfo(info: GroundInfo | null) {
    this.info = info;
    if (!info) return;
    this.wetY.value = info.sea_level;
    if (info.slope_gentle !== undefined && info.slope_moderate !== undefined && info.beach_h !== undefined) {
      this.clip.setParams({ slopeGentle: info.slope_gentle, slopeModerate: info.slope_moderate, beachHeight: info.beach_h, seaLevel: info.sea_level });
    }
  }
  /** Three-y of the wet level. The renderer eases it: up with the tide at once, down slowly. */
  setWetLevel(y: number) { this.wetY.value = y; }
  /** Draw order of the ground (1: after trees and other things that stand on it, 0: with them). */
  setGroundOrder(n: number) { this.groundOrder = n; for (const r of this.rings) r.mesh.renderOrder = n; }
  /** Scale the reach of the fine bump (1 = full; lower = fewer pixels pay for it, 0 = none). */
  setDetail(k: number) { this.bump.value = Math.min(1, Math.max(0, k)); }
  /** Rings 0 to k cast shadows (the others are too far for the cascades to reach and are left out of their passes). */
  setShadowRings(k: number) {
    this.shadowRings = Math.max(-1, Math.min(RINGS - 1, Math.floor(k)));
    for (const r of this.rings) r.mesh.castShadow = r.u.uRingK.value <= this.shadowRings;
  }

  /** Ground height (three-y) under three-space (x, z); 0 before there is ground. */
  heightAtThree(x: number, zThree: number): number { return this.clip.heightAt(x, zThree); }

  private makeRing(k: number): RingMesh {
    const dx = 0.04 * 2 ** k;
    const u = {
      uRingK: { value: k }, uDx: { value: dx }, uBand: { value: new THREE.Vector4(0, 0, RING_N - 1, RING_N - 1) },
      uUp: { value: new THREE.Vector2() }, uHasUp: { value: 0 },
    };
    const shared = {
      ...this.clip.uniforms,
      ...this.canopy.uniforms,
      uGPal: { value: this.palette },
      uGWetY: this.wetY, uGAlbedo: { value: GROUND_ALBEDO }, uGRough: { value: GROUND_ROUGH }, uGSpec: { value: GROUND_SPECULAR },
      uGBump: this.bump, uGBumpFade: { value: new THREE.Vector2(...BUMP_FADE) },
    };
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', this.positions);
    geo.setIndex(new THREE.BufferAttribute(new Uint16Array(0), 1));
    const mat = new THREE.MeshStandardMaterial({ roughness: 1.0, metalness: 0.0 });
    mat.polygonOffset = true; mat.polygonOffsetFactor = k; mat.polygonOffsetUnits = k;      // the coarser the ring, the farther back it is pushed where it overlaps a finer one
    mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, shared, u);
      Object.assign(shader.uniforms, this.shade.uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n' + RING_VERT_DECL)
        .replace('#include <beginnormal_vertex>', RING_VERT_NORMAL)
        .replace('#include <begin_vertex>', RING_VERT_BEGIN);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', GROUND_FRAG_COMMON)
        .replace('#include <color_fragment>', GROUND_FRAG_COLOR)
        .replace('#include <roughnessmap_fragment>', GROUND_FRAG_ROUGH)
        .replace('#include <lights_fragment_end>', GROUND_FRAG_SPEC)
        .replace('#include <normal_fragment_maps>', GROUND_FRAG_NORMAL);
    };
    mat.customProgramCacheKey = () => 'terrain-clip-v5';
    const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
    depth.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, shared, u);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n' + RING_VERT_DECL)
        .replace('#include <begin_vertex>', DEPTH_VERT_BEGIN);
    };
    depth.customProgramCacheKey = () => 'terrain-clip-depth-v2';
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = `ground ring ${k}`;
    mesh.renderOrder = this.groundOrder;
    mesh.customDepthMaterial = depth;
    mesh.receiveShadow = true;
    mesh.castShadow = k <= this.shadowRings;
    mesh.frustumCulled = false;       // the vertices are placed by the shader; the rings are culled by hand in update()
    mesh.visible = false;
    this.scene.add(mesh);
    return { mesh, geo, mat, depth, u, box: new THREE.Box3() };
  }

  private rebuild() {
    for (let k = 0; k < RINGS; k++) {
      const r = this.rings[k]!, L: RingLayout | null = this.clip.ring(k);
      // A ring with no window yet is only hidden. (Its draw range must be left alone: the windows come coarse first over a second or so, so the finer rings are without one at the first
      // layout, and a draw range of 0 set here would stay on the geometry for good and keep the ring from ever being drawn once its window came.)
      if (!L) { r.mesh.visible = false; continue; }
      const m = L.win.meta;
      r.geo.dispose();
      // The hole is one cell smaller on every side than the finer ring's edge, so the two rings overlap by a cell and no pixel can fall between them at the seam (the finer ring's heights are the
      // coarser one's there, see gcBandWeight; the polygon offset below lets the finer ring win the depth test). A hole of two cells or fewer is closed.
      const H = L.hole, ov: IRect | null = H && H[2] - H[0] > 2 && H[3] - H[1] > 2 ? [H[0] + 1, H[1] + 1, H[2] - 1, H[3] - 1] : null;
      r.geo.setIndex(new THREE.BufferAttribute(ringIndex(L.o, ov), 1));
      r.geo.setDrawRange(0, Infinity);       // the whole index buffer, whatever the geometry was set to before
      r.mesh.position.set(m.x0, 0, -m.y0);
      r.mesh.updateMatrix();
      r.u.uBand.value.set(L.o[0], L.o[1], L.o[2], L.o[3]);
      r.u.uHasUp.value = L.up ? 1 : 0;
      if (L.up) r.u.uUp.value.set(L.up[0], L.up[1]);
      const ext = (RING_N - 1) * m.dx;
      r.box.min.set(m.x0, L.lo, -m.y0 - ext);
      r.box.max.set(m.x0 + ext, L.hi, -m.y0);
      r.mesh.visible = true;
    }
    this.builtVersion = this.clip.layoutVersion;
    // The flat plane (the old tile's brown ground) is only what is drawn until the first window comes. The rings then reach past the horizon, and the plane, which the frame loop keeps at the
    // sea's level, would hide the sea floor wherever it lies lower and show through wherever a ring is not drawn.
    this.plane.visible = !this.clip.ready;
    if (this.grid) this.grid.visible = !this.clip.ready;
  }

  /** The ground cannot be coloured until the server's ground_info has given the land file's slope thresholds. The windows can get here first, so wait for it (nothing is drawn meanwhile); but a ground_info
   *  that came without them, or none within PARAMS_WAIT_MS of the first window, is an error that says so. */
  private awaitParams(): void {
    if (!this.clip.ready) return;
    if (this.info) {
      const i = this.info;
      if (i.slope_gentle === undefined || i.slope_moderate === undefined || i.beach_h === undefined) {
        throw new Error(`the ground_info has no slope_gentle, slope_moderate and beach_h (the land file's artist block): it has only ${Object.keys(i).join(', ')}. The server that sent it is not running the broadcast_server.py this client was built with.`);
      }
      throw new Error(`the ground_info's slope_gentle ${i.slope_gentle}, slope_moderate ${i.slope_moderate}, beach_h ${i.beach_h} were rejected (see the '[sim] ground_info rejected' error before this one)`);
    }
    const now = performance.now();
    if (this.paramsSince < 0) this.paramsSince = now;
    else if (now - this.paramsSince > PARAMS_WAIT_MS) throw new Error(`ground windows have come but no ground_info in ${PARAMS_WAIT_MS / 1000} s (the server sends it first, when a client connects)`);
  }

  /** Once per frame, after the floating origin has moved and before drawing: take new windows into the textures and the layout, and leave out the rings that are out of the view. */
  update(camera: THREE.PerspectiveCamera): void {
    this.clip.sync(origin.org.x, origin.org.y);
    this.canopy.sync(origin.org.x, origin.org.y);
    if (!this.clip.hasParams) { this.awaitParams(); return; }
    this.paramsSince = -1;
    if (this.builtVersion !== this.clip.layoutVersion) this.rebuild();
    camera.updateMatrixWorld();
    this.pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.pv);
    const cx = camera.position.x, cz = camera.position.z, R = planet.radius;
    let n = 0;
    for (let k = 0; k < RINGS; k++) {
      const r = this.rings[k]!;
      if (!this.clip.ring(k)) continue;
      if (k > this.shadowRings) {                // a ring that casts shadows is drawn into the cascades too: never leave it out
        this.box.copy(r.box);
        // the picture bends the ground down with distance from the eye (planet.ts): the box goes down by the drop at its farthest corner
        const fx = Math.max(Math.abs(this.box.min.x - cx), Math.abs(this.box.max.x - cx)), fz = Math.max(Math.abs(this.box.min.z - cz), Math.abs(this.box.max.z - cz));
        this.box.min.y -= dropAt(Math.hypot(fx, fz), R);
        r.mesh.visible = this.frustum.intersectsBox(this.box);
      } else r.mesh.visible = true;
      if (r.mesh.visible) n++;
    }
    this.drawn = n;
  }

  dispose() {
    for (const r of this.rings) {
      this.scene.remove(r.mesh);
      r.geo.dispose(); r.mat.dispose(); r.depth.dispose();
    }
    this.rings.length = 0;
    this.plane.visible = true;
    this.plane.position.y = 0;
    if (this.grid) this.grid.visible = true;
    if (this.ownsShade) this.shade.dispose();
    if (this.ownsCanopy) this.canopy.dispose();
  }
}
